import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DgaIcon,
  DgaButton,
  DgaRectangleSkeleton,
  DgaSquareSkeleton,
  DgaLineSkeleton,
} from "platformscode-new-react";
import EventCard from "../../../components/shared/EventCard";
import { getHomeEvents } from '../../../services/api';
import moment from "moment-hijri";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const PrevArrow = (props) => (
  <button {...props} className="custom-arrow left-arrow">
    <DgaIcon color="#fff" icon="arrow-left-01" size={24} type="" variant="stroke" />
  </button>
);

const NextArrow = (props) => (
  <button {...props} className="custom-arrow right-arrow">
    <DgaIcon color="#fff" icon="arrow-right-01" size={24} type="" variant="stroke" />
  </button>
);

const isExpired = (dateStr) => {
  const parts = String(dateStr || "").split("إلى :");
  const endDateHijri = parts[1]?.trim();
  if (!endDateHijri) return false;
  const endDateGregorian = moment(endDateHijri, "iYYYY-iMM-iDD").toDate();
  endDateGregorian.setHours(23, 59, 59, 999);
  return endDateGregorian < new Date();
};

function EventsSection() {
  const sectionRef = useRef(null);
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Defer API fetch until the section is near the viewport.
  // Prevents event card images from downloading during the LCP window.
  useEffect(() => {
    let cancelled = false;

    const doFetch = async () => {
      try {
        const data = await getHomeEvents();
        if (!cancelled) setEvents(data.data || []);
      } catch (err) {
        if (!cancelled) {
          console.error("خطأ في تحميل الفعاليات:", err);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) {
      doFetch();
      return () => { cancelled = true; };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        doFetch();
      },
      { rootMargin: '600px' }
    );
    observer.observe(el);
    return () => { cancelled = true; observer.disconnect(); };
  }, []);

  const visible     = useMemo(() => (Array.isArray(events) ? events.slice(0, 3) : []), [events]);
  const slidesToShow = Math.min(visible.length || 1, 3);

  const sliderSettings = useMemo(
    () => ({
      rtl: true,
      dots: false,
      arrows: false,
      nextArrow: <PrevArrow />,
      prevArrow: <NextArrow />,
      speed: 400,
      slidesToShow,
      slidesToScroll: 1,
      initialSlide: 0,
      swipeToSlide: true,
      centerMode: false,
      adaptiveHeight: false,
      responsive: [
        { breakpoint: 1260, settings: { slidesToShow: Math.min(visible.length || 1, 2), arrows: true } },
        { breakpoint: 765,  settings: { slidesToShow: 1, arrows: true } },
        { breakpoint: 640,  settings: { slidesToShow: 1, arrows: true } },
      ],
    }),
    [visible.length, slidesToShow]
  );

  const allExpired = visible.length > 0 && visible.every((i) => isExpired(i.date));

  return (
    <div id="events-section" ref={sectionRef} style={{ padding: "80px 0px 100px 0px" }} dir="rtl">
      {loading ? (
        <div className="cw-full">
          <h2 className="display-sm-bold text-[#161616] mb-4" aria-label="الفعاليات">
            <DgaRectangleSkeleton size="md" type="1" width="long" />
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="grid gap-[24px] rounded-lg"
                style={{ border: "1.5px solid #0c111b17", padding: "16px" }}
              >
                <DgaSquareSkeleton
                  type="1"
                  style={{ width: "372.667px", height: "240px", borderRadius: "10px" }}
                />
                <div className="grid gap-[8px]">
                  <DgaLineSkeleton type="1" size="sm" className="mb-1" />
                  <DgaRectangleSkeleton size="md" type="1" width="long" className="mb-2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : error || !visible.length || allExpired ? null : (
        <div className="cw-full">
          <div className="m-4 flex justify-between items-center">
            <div data-aos="fade-in" className="flex gap-[8px] mb-4">
              <h2 className="display-sm-bold text-[#161616]">الفعاليات</h2>
            </div>
            <DgaButton
              label="عرض الكل"
              variant="secondary-outline"
              size="lg"
              onClick={() => (window.location.href = "/events")}
            />
          </div>
          <div data-aos="fade-in" className="relative">
            <Slider {...sliderSettings}>
              {visible.map((item) => (
                <div key={item.id} className="px-2 rtl">
                  <EventCard data={item} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      )}
      <style>{`
        .slick-slide { outline: none; }
        .slick-dots { bottom: -30px; }
        .slick-arrow { width: 36px; height: 36px; }
        .slick-prev:before, .slick-next:before { font-size: 28px; }
        .rtl { direction: rtl !important; }
      `}</style>
    </div>
  );
}

export default EventsSection;
